import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { editReplyOnMessage, getMessageDetailsForAdmin } from "../../apis";
import Modal from "react-modal";
import { formatDateForChat } from "../../_helper";
import useReplies from "./useReplies";

const EditReplyForAdmin = ({
  currentMessageId,
  replyData,
  setIsModalOpenForEdit,
  isModalOpenForEdit,
  setRepliesData,
  setPagination,
}) => {
  const { fetchReplies } = useReplies();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const formik = useFormik({
    initialValues: {
      message: replyData.message || "",
    },
    validationSchema: Yup.object({
      message: Yup.string().trim().required("Message is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("message", values.message);
      if (selectedImage) {
        formData.append("message_attache", selectedImage);
      }

      setIsSubmitting(true);
      try {
        await editReplyOnMessage(replyData?.id, formData);
        resetForm();
        setSelectedImage(null);
        fetchReplies({
          messageId: currentMessageId,
          scrollToBottom: true,
        }).then((result) => {
          if (result) {
            const { repliesData, pagination } = result;
            setRepliesData(repliesData);
            setPagination(pagination);
          }
        });
        closeModal();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to send message",
          text: error.response?.data?.message || "Please try again later.",
          confirmButtonColor: "#d33",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const closeModal = () => {
    setSelectedImage(null);
    setIsModalOpenForEdit(false);
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForEdit}
        shouldCloseOnOverlayClick={false}
        onRequestClose={closeModal}
        shouldCloseOnEsc={true}
        contentLabel="Edit Message"
        className="tw-bg-white tw-rounded-lg tw-p-4 tw-max-w-md tw-w-full tw-mx-auto tw-my-auto tw-relative"
        overlayClassName="tw-fixed tw-inset-0 tw-bg-black/70 tw-z-50 tw-flex tw-items-center tw-justify-center"
      >
        {/* Close Button */}
        <button
          onClick={closeModal}
          className="tw-absolute !tw-p-0 tw-text-white tw-bg-red-500 tw-rounded-full tw-items-center tw-w-7 tw-h-7 tw-flex tw-justify-center -tw-top-10 -tw-right-10 hover:tw-text-gray-300"
        >
          <span className="material-icons !tw-text-sm">close</span>
        </button>

        <div className="tw-flex tw-items-end tw-justify-end tw-mb-4 tw-relative">
          <div className="tw-min-w-24 tw-relative tw-max-w-[75%] tw-bg-secondary tw-text-white tw-rounded-lg tw-px-3 tw-py-2 tw-shadow">
            <p className="tw-mb-4">{replyData?.message}</p>

            {replyData?.messageAttache && (
              <img
                src={replyData.messageAttache}
                alt="attachment"
                className="tw-mb-4 tw-rounded-lg tw-max-w-full tw-max-h-60 hover:tw-opacity-90 tw-transition"
              />
            )}

            <span className="tw-absolute tw-bottom-1 tw-right-2 tw-text-[11px] tw-text-gray-200">
              {formatDateForChat(replyData.createdAt)}
            </span>
            <div className="tw-absolute tw-right-[-8px] tw-bottom-2 tw-w-0 tw-h-0 tw-border-t-8 tw-border-t-transparent tw-border-b-8 tw-border-b-transparent tw-border-l-8 tw-border-l-secondary"></div>
          </div>

          <div className="tw-w-9 tw-h-9 tw-rounded-full tw-ml-2">
            <img
              src={replyData?.userImage}
              alt="User"
              className="tw-w-9 tw-h-9 tw-rounded-full"
            />
          </div>
        </div>

        {/* If new image selected */}
        {selectedImage && (
          <img
            src={URL.createObjectURL(selectedImage)}
            alt="Selected Preview"
            className="tw-max-w-full tw-h-auto tw-m-auto tw-rounded-lg tw-mb-3"
          />
        )}

        {/* Edit Input */}
        <form onSubmit={formik.handleSubmit} className="tw-mt-2 tw-text-end">
          <input
            type="text"
            name="message"
            placeholder="Edit your message..."
            className="tw-w-full tw-p-2 tw-rounded-md tw-border tw-focus:outline-none tw-focus:border-blue-500"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.message}
          />
          {formik.touched.message && formik.errors.message && (
            <div className="tw-text-red-500 tw-text-start tw-mt-1">
              {formik.errors.message}
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`${
              formik.touched.message && formik.errors.message
                ? "tw-bg-gray-400"
                : "tw-bg-primary"
            } tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-mt-2 ${
              isSubmitting ? "tw-opacity-50 tw-cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Editing..." : "Edit"}
          </button>
        </form>
      </Modal>
    </>
  );
};

export default EditReplyForAdmin;
